import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SponsoredRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { PackBox } from '@/components/ui/PackBox';
import { useSector, useSectorPath } from '@/lib/sector';

/**
 * Advertisements, in their own labelled block.
 *
 * ── WHY THIS IS A SEPARATE COMPONENT AND A SEPARATE CALL ────────────────────
 *
 * Because the one thing ChiCare cannot be is a place where a buyer pays more
 * for the same medicine because a pharmacy paid us. The offer ladder on a
 * product page is ordered by what the buyer pays and says so on the page.
 *
 * Keeping advertisements in their own endpoint, their own component and their
 * own visually distinct block is what makes mixing them into a price list an
 * act somebody would have to take deliberately rather than by accident. The
 * API helps: a sponsored row carries no price at all, so there is nothing to
 * sort by even if somebody tried.
 *
 * ── AND WHY THE LABEL IS NOT SUBTLE ─────────────────────────────────────────
 *
 * "Sponsored" is in the heading, in the disclosure line, and on every card.
 * A buyer who later works out that a placement was paid for, and was not told,
 * does not conclude they misread it — they conclude the prices are rigged too.
 */

interface Props {
  placement: 'home' | 'search' | 'category';
  kind?: 'shop' | 'product';
  categoryId?: number;
  className?: string;
}

export function SponsoredBlock({ placement, kind = 'product', categoryId, className }: Props) {
  const sector = useSector();
  const path = useSectorPath();

  const { data } = useQuery({
    queryKey: ['sponsored', placement, kind, sector, categoryId ?? null],
    queryFn: ({ signal }) =>
      api.sponsored({ placement, kind, sector, category_id: categoryId }, signal),
    // Advertisements change on a schedule measured in days, so this does not
    // need to be fresh on every navigation. Five minutes keeps a busy buyer
    // from re-fetching the same four rows all afternoon.
    staleTime: 5 * 60 * 1000,
  });

  const rows = data?.rows ?? [];

  // Nothing sold for this placement is not an empty state worth drawing. A
  // grey "no advertisements" box would be ChiCare advertising its own
  // unsold inventory to buyers.
  if (rows.length === 0) {
    return null;
  }

  return (
    <section
      className={cn('rounded-card border border-line bg-white p-3.5', className)}
      aria-label="Sponsored"
      data-testid={`sponsored-${placement}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
          Sponsored
        </h2>
      </div>

      {/* The disclosure comes from the API so every client says the same thing
          rather than each inventing its own wording. */}
      <p className="m-0 mt-1 text-[11px] leading-snug text-faint">{data?.disclosure}</p>

      <div
        className={cn(
          'mt-3 grid gap-2.5',
          kind === 'product' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4' : 'sm:grid-cols-2',
        )}
      >
        {rows.map((row, index) =>
          row.product ? (
            <SponsoredProduct key={row.product.id} row={row} to={path(`/products/${row.product.slug}`)} />
          ) : (
            <SponsoredShop key={row.pharmacy.id ?? index} row={row} path={path} />
          ),
        )}
      </div>
    </section>
  );
}

function SponsoredProduct({ row, to }: { row: SponsoredRow; to: string }) {
  const product = row.product!;

  return (
    <Link to={to} className="group no-underline" data-testid={`sponsored-product-${product.id}`}>
      <div className="relative aspect-square overflow-hidden rounded-lg bg-paper">
        {product.image ? (
          <img
            src={product.image}
            alt=""
            aria-hidden
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
          />
        ) : (
          <PackBox pack="para" square rounded="lg" />
        )}

        {/* On the card as well as the heading. A card that travels — into a
            screenshot, a share, a scroll where the heading is off screen —
            must still say what it is. */}
        <span className="absolute left-1 top-1 rounded-pill bg-ink/75 px-1.5 py-px text-[9px] font-semibold uppercase tracking-[0.06em] text-white">
          {row.label}
        </span>
      </div>

      <p className="m-0 mt-1.5 line-clamp-2 text-[12px] font-semibold leading-snug text-ink">
        {product.name}
      </p>

      {/* Where to buy it, never what it costs. The price comes from the ladder
          on the product page, cheapest first. */}
      <p className="m-0 mt-0.5 truncate text-[10.5px] text-muted">{row.pharmacy.name}</p>
    </Link>
  );
}

function SponsoredShop({ row, path }: { row: SponsoredRow; path: (to: string) => string }) {
  return (
    <Link
      to={path(`/pharmacies/${row.pharmacy.slug}`)}
      className="flex min-h-11 items-center gap-2.5 rounded-card border border-line p-2.5 no-underline hover:border-blue"
      data-testid={`sponsored-shop-${row.pharmacy.id}`}
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-tint">
        <Icon name="store" size={16} className="text-blue" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold text-ink">
          {row.pharmacy.name}
        </span>
        <span className="block truncate text-[10.5px] text-muted">
          {[row.pharmacy.lga, row.pharmacy.state].filter(Boolean).join(', ')}
        </span>
      </span>

      <span className="shrink-0 rounded-pill bg-paper px-1.5 py-px text-[9px] font-semibold uppercase tracking-[0.06em] text-faint">
        {row.label}
      </span>
    </Link>
  );
}
